import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Stack,
  Avatar,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { FaRocketchat } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getData } from "../../Webservice/Webservice";

const Queries = () => {
  const navigate = useNavigate();
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [tabLoading, setTabLoading] = useState(false);

  const fetchQueries = async (status) => {
    try {
      setTabLoading(true);
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      const response = await getData(`query/list?status=${status}`, null, token);
      if (response) {
        setQueries(response.queries || []);
      } else {
        setError("Failed to fetch queries");
      }
    } catch (err) {
      setError("An error occurred while fetching queries");
    } finally {
      setTabLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitialQueries = async () => {
      try {
        setLoading(true);
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await getData("query/list?status=open", null, token);
        if (response) {
          setQueries(response.queries || []);
        } else {
          setError("Failed to fetch queries");
        }
      } catch (err) {
        setError("An error occurred while fetching queries");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialQueries();
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const statuses = ['open', 'in-progress', 'resolved'];
    fetchQueries(statuses[newValue]);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f7f9", minHeight: "100vh" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            color: "text.primary",
            fontFamily: "Mulish, sans-serif",
            fontWeight: 600,
          }}
        >
          Support Tickets
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontFamily: "Mulish, sans-serif",
              fontWeight: 600,
              fontSize: "1rem",
              minHeight: 48,
            },
            "& .Mui-selected": {
              color: "rgb(255, 209, 2) !important",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "rgb(255, 209, 2)",
              height: 3,
              borderRadius: "2px 2px 0 0",
            },
          }}
        >
          <Tab label="Open" />
          <Tab label="In-Progress" />
          <Tab label="Resolved" />
        </Tabs>
      </Box>

      {/* Loading indicator for tab switching */}
      {tabLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}

      <Stack>
        {!tabLoading && queries.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                fontFamily: "Mulish, sans-serif",
                mb: 1,
              }}
            >
              No {activeTab === 0 ? 'Open' : activeTab === 1 ? 'In-Progress' : 'Resolved'} Support Tickets
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontFamily: "Mulish, sans-serif",
                opacity: 0.7,
              }}
            >
              {activeTab === 0 
                ? "No new queries to review at the moment."
                : activeTab === 1 
                ? "No queries are currently in progress."
                : "No resolved queries to display."
              }
            </Typography>
          </Box>
        ) : (
          queries.map((query) => (
            <Card
              key={query._id}
              sx={{
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                  }}
                >
                  {/* Left side - Name and Description */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      width: "350px",
                    }}
                  >
                    <Avatar sx={{ bgcolor: "rgb(255, 209, 2)" }}>
                      {query.customerId?.name?.charAt(0) || "N/A"}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "Mulish, sans-serif",
                          fontWeight: 700,
                          color: "text.primary",
                          mb: 0.5,
                        }}
                      >
                        {query.subject || "General Inquiry"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontFamily: "Mulish, sans-serif",
                          mb: 1,
                          fontWeight: 600
                        }}
                      >
                        By: {query.customerId?.name || "Unknown Customer"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontFamily: "Mulish, sans-serif",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {query.description}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right side - Reference Number, Order Number, and View button */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      flex: 1,
                      justifyContent: "flex-end"
                    }}
                  >
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          fontWeight: 700,
                          textTransform: "uppercase"
                        }}
                      >
                        Reference
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.primary",
                          fontFamily: "Mulish, sans-serif",
                          fontWeight: 600
                        }}
                      >
                        {query.referenceNo}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          fontWeight: 700,
                          textTransform: "uppercase"
                        }}
                      >
                        Order ID
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: query.orderId ? "primary.main" : "text.secondary",
                          fontFamily: "Mulish, sans-serif",
                          fontWeight: 600
                        }}
                      >
                        {query.orderId?.orderNumber || "No Order"}
                      </Typography>
                    </Box>

                    <Button
                      startIcon={<FaRocketchat />}
                      onClick={() => navigate(`/queries/chat/${query._id}`)}
                      sx={{
                        textTransform: "none",
                        borderRadius: "50px",
                        px: 3,
                        py: 1.2,
                        fontWeight: 600,
                        color: "black",
                        background: "rgb(255, 209, 2)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: "rgba(255, 209, 2, 0.8)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      Open Chat
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Stack>
    </Box>
  );
};

export default Queries;